import { useCallback, useEffect, useMemo, useState } from "react";
import type { PerpsMarginMode, PerpsOrderDetailItem, PerpsOrderType, PerpsSide } from "@cede/trading-ui";
import { FEATURE_FLAGS, MarginMode, ORDER_SIDE, OrderType, SocialName } from "@cede/types";
import type { MarketPairV2 } from "@cede/types";
import { CEDE_CEXS, type EXCHANGES } from "@cede/utils";
import { useAccountFromExchangeId } from "@/hooks/accounts/useAccountFromExchangeId";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { isDirectWSSupported, useExchangeOrderBookStream } from "@/hooks/orderbook/direct-ws";
import { getPerpsExchangeConfig } from "@/hooks/perps/cex-perps/config";
import { useAutoSelectMarginMode } from "@/hooks/perps/cex-perps/useAutoSelectMarginMode";
import { useCexPerpsBalances } from "@/hooks/perps/cex-perps/useCexPerpsBalances";
import { useCexPerpsMarginAssetMode } from "@/hooks/perps/cex-perps/useCexPerpsMarginAssetMode";
import { useCexPerpsMarkPrice } from "@/hooks/perps/cex-perps/useCexPerpsMarkPrice";
import { useCexPerpsMarket } from "@/hooks/perps/cex-perps/useCexPerpsMarket";
import { useCexPerpsMaxLeverage } from "@/hooks/perps/cex-perps/useCexPerpsMaxLeverage";
import { useCexPerpsTrade } from "@/hooks/perps/cex-perps/useCexPerpsTrade";
import { useMarketRate } from "@/hooks/perps/cex-perps/useMarketRate";
import { useSocialAccountRequirement } from "@/hooks/socials/useSocialAccountRequirement";
import { useCexPerpsTradingFormStore } from "@/store/cex-perps/cexPerpsTradingFormStore";
import { useConnectStore, STEPS } from "@/store/connectStore";
import { useLeveragePreferencesStore } from "@/store/leveragePreferencesStore";
import { safeDecimal } from "@/utils/hyperliquid";
import Decimal from "decimal.js";
import { enqueueSnackbar } from "notistack";

type CexPerpsOrderSide = "long" | "short";

interface UseCexPerpsFormLogicParams {
  exchangeId: string;
  campaignId?: string;
  /** Social accounts the campaign requires (empty/undefined → no social gate). */
  requiredSocialAccounts?: SocialName[];
  /**
   * Per-campaign limit-order gate read from `currentEpoch.limitOrder` (the
   * epoch inherits the epoch group's value). Only an explicit `false`
   * disables limit orders; `undefined`/`true` keep them enabled. OR'd with
   * the global `PERPS_HUB_DISABLE_LIMIT_ORDERS` kill-switch.
   */
  allowLimitOrders?: boolean;
  initialSide?: CexPerpsOrderSide;
  open?: boolean;
  onConnect?: () => void;
  onSubmitSuccess?: () => void;
  showMissingInfoToast?: boolean;
}

export interface CexPerpsFormLogic {
  exchangeId: string;
  config: ReturnType<typeof getPerpsExchangeConfig>;
  selectedMarket: MarketPairV2 | null;
  isAccountConnected: boolean;
  isCreatingOrder: boolean;
  isBalanceLoading: boolean;
  maxLeverage: number;

  orderType: OrderType;
  perpsOrderType: PerpsOrderType;
  orderSide: CexPerpsOrderSide;
  perpsSide: PerpsSide;
  marginMode: MarginMode;
  perpsMarginMode: PerpsMarginMode;
  marginModeReadOnly: boolean;
  /** Binance in Multi-Assets Mode rejects isolated margin (-4168), so the selector is locked to cross. */
  isIsolatedMarginBlocked: boolean;
  leverage: number;
  size: string;
  price: string;
  quoteAmount: string;
  usdEquivalent: string | undefined;
  sizeMode: "base" | "quote";
  sizePercentage: number;
  currentPrice: Decimal;

  balanceDisplay: string;
  marketLabel: string;
  validationError: string | null;
  /** Set when the order size is below the exchange minimum; also blocks submission. */
  amountError: string | null;
  orderDetailItems: PerpsOrderDetailItem[];

  buttonVariant: "primary" | "destructive";
  buttonText: string;
  isSubmitDisabled: boolean;
  isLimitOrdersDisabled: boolean;
  /** True when the campaign requires social accounts the user hasn't connected. */
  shouldBlockSocials: boolean;
  /** Opens the social-account linking modal (LinkSocials step). */
  handleConnectSocials: () => void;

  leverageModalOpen: boolean;
  setLeverageModalOpen: (open: boolean) => void;
  setSizeMode: (mode: "base" | "quote") => void;
  setPrice: (price: string) => void;
  handleOrderTypeChange: (type: PerpsOrderType) => void;
  handleMarginModeChange: (mode: PerpsMarginMode) => void;
  handleSideChange: (side: PerpsSide) => void;
  handleUnifiedSizeChange: (value: string) => void;
  handlePercentageChange: (pct: number) => void;
  handleLeverageConfirm: (newLeverage: number) => void;
  handleOrderSubmit: () => Promise<void>;
  handleConnectAccount: () => void;
}

const getCurrentPrice = (marketRate: { ask: string; bid: string } | undefined, side: CexPerpsOrderSide) =>
  marketRate ? new Decimal(side === "long" ? marketRate.ask : marketRate.bid) : new Decimal(0);

const getIsPositiveSize = (size: string) => {
  try {
    return !!size && new Decimal(size).gt(0);
  } catch {
    return false;
  }
};

/**
 * User-facing error when the order size is below the exchange minimum, else null.
 * Drives both the inline form error and the submit-button gating so the displayed
 * warning and the disabled state never disagree. Skipped when the minimum is unknown
 * (market not yet loaded), leaving the SDK as the backstop.
 */
const getMinOrderSizeError = (
  size: string,
  minOrderSize: number | undefined,
  base: string | undefined,
): string | null => {
  if (!size || !minOrderSize) return null;
  try {
    const sizeDecimal = new Decimal(size);
    if (sizeDecimal.gt(0) && sizeDecimal.lt(minOrderSize)) {
      return `Minimum amount is ${minOrderSize} ${base ?? ""}`.trim();
    }
  } catch {
    return null;
  }
  return null;
};

// Safety headroom reserved in Max sizing on top of the open fee, absorbing the mark drift between
// sizing and the venue's order-time margin check (a max order otherwise sits at ~100% of margin and
// -2019s when the mark ticks up in that window). Scales with leverage: higher-leverage max orders
// reserve less margin per token, so the same tick eats a larger share of their headroom — grow the
// cushion linearly. factor = 1 + BASE + PER_LEVERAGE·leverage (L5 → 1.0%, L10 → 1.5%, L20 → 2.5%).
const MARGIN_SAFETY_BASE = new Decimal("0.005");
const MARGIN_SAFETY_PER_LEVERAGE = new Decimal("0.001");

/**
 * Percentage of the user's margin used by an order of `notional` quote value.
 * Returns a rounded integer clamped to [0, 100].
 */
const computeSizePercentage = (notional: Decimal, balance: Decimal, leverage: number): number => {
  if (!balance.gt(0) || !notional.gt(0) || leverage <= 0) return 0;
  const pct = notional.div(leverage).div(balance).mul(100);
  return Math.min(100, Math.round(pct.toNumber()));
};

export function useCexPerpsFormLogic({
  exchangeId,
  campaignId,
  requiredSocialAccounts: requiredSocialAccountsProp,
  allowLimitOrders,
  initialSide = "long",
  open,
  onConnect,
  onSubmitSuccess,
  showMissingInfoToast = false,
}: UseCexPerpsFormLogicParams): CexPerpsFormLogic {
  const config = getPerpsExchangeConfig(exchangeId);
  const { selectedMarket } = useCexPerpsMarket();
  const account = useAccountFromExchangeId(exchangeId);
  const isAccountConnected = !!account;
  const accountMaxLeverage = useCexPerpsMaxLeverage(exchangeId, account?.id?.toString(), selectedMarket?.pairSymbol);
  const maxLeverage = accountMaxLeverage ?? selectedMarket?.maxLeverage ?? config?.maxLeverage ?? 50;

  // Campaign social-account gate: when the campaign requires social accounts the
  // user hasn't connected (and isn't whitelisted), the submit button is replaced
  // by a "Connect social accounts" CTA — same rule the backend enforces when
  // indexing trades, so ineligible orders are stopped before they're placed.
  // The required list is supplied by the caller (which already holds the campaign).
  const requiredSocialAccounts = requiredSocialAccountsProp ?? [];
  const { shouldBlockTrading: shouldBlockSocials, openSocialLinkingModal: handleConnectSocials } =
    useSocialAccountRequirement({ requiredSocialAccounts, enabled: requiredSocialAccounts.length > 0 });

  const {
    orderType,
    leverage,
    marginMode,
    size,
    price,
    validationError,
    setOrderType,
    setLeverage,
    setMarginMode,
    setSize,
    setPrice,
    reset: resetForm,
  } = useCexPerpsTradingFormStore();

  const { setOpenConnectModal, setActiveStep, setExchangeIds } = useConnectStore();
  const { getLeverage: getPreferredLeverage, setLeverage: savePreferredLeverage } = useLeveragePreferencesStore();
  const { createOrder, isCreatingOrder } = useCexPerpsTrade(exchangeId);

  const [orderSide, setOrderSide] = useState<CexPerpsOrderSide>(initialSide);
  const [leverageModalOpen, setLeverageModalOpen] = useState(false);
  const [sizePercentage, setSizePercentage] = useState(0);
  const [sizeMode, setSizeMode] = useState<"base" | "quote">("quote");

  useEffect(() => {
    if (open) {
      setOrderSide(initialSide);
    }
  }, [open, initialSide]);

  useEffect(() => {
    if (!selectedMarket?.pairSymbol) return;

    const preferred = getPreferredLeverage(exchangeId, selectedMarket.pairSymbol, maxLeverage);
    setLeverage(preferred);
  }, [exchangeId, getPreferredLeverage, maxLeverage, selectedMarket?.pairSymbol, setLeverage]);

  const settlementToken = selectedMarket?.settlementToken?.toUpperCase() ?? config?.settlementToken ?? "";
  const { available: balance, isLoading: isBalanceLoading } = useCexPerpsBalances({
    exchangeId,
    accountId: account?.id,
    settlementToken,
    enabled: !!settlementToken,
    marginMode,
  });

  useAutoSelectMarginMode({
    exchangeId,
    accountId: account?.id ? Number(account.id) : undefined,
    pairSymbol: selectedMarket?.pairSymbol,
    enabled: isAccountConnected,
  });

  const marginAssetMode = useCexPerpsMarginAssetMode(exchangeId, account?.id);

  const { marketRate } = useMarketRate({ exchangeId, pairSymbol: selectedMarket?.pairSymbol ?? "" });
  const currentPrice = useMemo(() => getCurrentPrice(marketRate, orderSide), [marketRate, orderSide]);

  // Near-realtime mark price — the exact price the venue reserves initial margin on. Sizing on the
  // book mid instead under-reserves whenever the mark sits above the mid (it oscillates ±several bps
  // around it), which alone can exceed a max order's tolerance and trigger -2019.
  const { markPrice: liveMarkPrice } = useCexPerpsMarkPrice({
    exchangeId,
    pairSymbol: selectedMarket?.pairSymbol,
    enabled: !!selectedMarket?.pairSymbol,
  });

  // Best bid/ask from the direct-WS order book (~100ms fresh) — the preferred margin-price source,
  // ahead of the 5s-polled mark. See the marginPrice memo below for the ordering rationale.
  const { orderBook: liveOrderBook } = useExchangeOrderBookStream({
    exchangeId,
    pairSymbol: selectedMarket?.pairSymbol ?? "",
    externalMarketId: selectedMarket?.externalMarketId ?? "",
    enabled: isDirectWSSupported(exchangeId) && !!selectedMarket?.pairSymbol,
  });

  // Margin-reservation price. Prefer the realtime WS book mid: it's ~100ms fresh, whereas the mark
  // is polled every 5s, and on a volatile pair 5s of drift dwarfs the tiny mid-vs-mark gap and is
  // what eats a max order's headroom (→ -2019). Fall back to the polled mark, then the execution quote.
  const marginPrice = useMemo(() => {
    // safeDecimal ignores malformed/non-finite feed values (→ 0) so a bad tick can't throw mid-render.
    const bestBid = safeDecimal(liveOrderBook?.bids?.[0]?.[0]);
    const bestAsk = safeDecimal(liveOrderBook?.asks?.[0]?.[0]);
    if (bestBid.gt(0) && bestAsk.gt(0)) {
      const mid = bestBid.plus(bestAsk).div(2);
      if (mid.gt(0)) return mid;
    }
    const mark = safeDecimal(liveMarkPrice);
    if (mark.gt(0)) return mark;
    return currentPrice;
  }, [liveMarkPrice, liveOrderBook, currentPrice]);

  // Max-margin divisor: reserve the open fee (1 + leverage·feeRate) plus a leverage-scaled safety
  // buffer for the mark drift between when Max is sized and when the venue checks margin at order
  // time. Without it a 100%-margin order sits at ~0 bps tolerance and -2019s on any uptick.
  const maxSizingDivisor = useMemo(() => {
    const safetyFactor = new Decimal(1).plus(MARGIN_SAFETY_BASE).plus(MARGIN_SAFETY_PER_LEVERAGE.mul(leverage));
    return new Decimal(1).plus(new Decimal(leverage).mul(new Decimal(config?.takerFee ?? 0.0005))).mul(safetyFactor);
  }, [config?.takerFee, leverage]);

  const quoteAmount = useMemo(() => {
    try {
      const sizeDecimal = size ? new Decimal(size) : new Decimal(0);
      if (sizeDecimal.gt(0) && marginPrice.gt(0)) {
        return sizeDecimal.mul(marginPrice).toFixed(2);
      }
    } catch {
      // Invalid size string.
    }
    return "";
  }, [size, marginPrice]);

  const handleSizeChange = useCallback(
    (value: string) => {
      setSize(value);

      if (!marginPrice.gt(0) || !balance) {
        setSizePercentage(0);
        return;
      }

      try {
        const newSize = value ? new Decimal(value) : new Decimal(0);
        const balanceDec = new Decimal(balance || "0");
        // Slider % tracks margin usage, so mirror handlePercentageChange: mark price + sizing divisor.
        const marginNotional = newSize.mul(marginPrice).mul(maxSizingDivisor);
        setSizePercentage(computeSizePercentage(marginNotional, balanceDec, leverage));
      } catch {
        setSizePercentage(0);
      }
    },
    [balance, maxSizingDivisor, leverage, marginPrice, setSize],
  );

  const handleUnifiedSizeChange = useCallback(
    (value: string) => {
      if (sizeMode !== "quote") {
        handleSizeChange(value);
        return;
      }

      try {
        const quoteVal = value ? new Decimal(value) : new Decimal(0);
        if (quoteVal.gt(0) && marginPrice.gt(0)) {
          setSize(quoteVal.div(marginPrice).toFixed(8));
        } else {
          setSize("");
        }
      } catch {
        setSize("");
      }

      if (!marginPrice.gt(0) || !balance) {
        setSizePercentage(0);
        return;
      }

      try {
        const quoteVal = value ? new Decimal(value) : new Decimal(0);
        const balanceDec = new Decimal(balance || "0");
        // quote input is a marginPrice-notional (size = quote / marginPrice), so margin usage is
        // quote × maxSizingDivisor / leverage — mirrors the mark-based sizing used everywhere else.
        const marginNotional = quoteVal.mul(maxSizingDivisor);
        setSizePercentage(computeSizePercentage(marginNotional, balanceDec, leverage));
      } catch {
        setSizePercentage(0);
      }
    },
    [balance, maxSizingDivisor, handleSizeChange, leverage, marginPrice, setSize, sizeMode],
  );

  const handlePercentageChange = useCallback(
    (pct: number) => {
      setSizePercentage(pct);
      if (!balance || marginPrice.lte(0)) return;
      try {
        const balanceDec = new Decimal(balance || "0");
        // Size on the mark (marginPrice) with the fee + safety divisor so initialMargin + openFee stay
        // under available margin even if the mark ticks up before the order is checked (else -2019).
        const newSize = balanceDec.mul(pct).div(100).div(marginPrice).mul(leverage).div(maxSizingDivisor);
        // Round the size DOWN: rounding up the last digit would nudge margin back over the edge.
        setSize(newSize.gt(0) ? newSize.toFixed(8, Decimal.ROUND_DOWN) : "");
      } catch {
        setSize("");
      }
    },
    [balance, maxSizingDivisor, leverage, marginPrice, setSize],
  );

  const handleLeverageConfirm = useCallback(
    (newLeverage: number) => {
      const oldLeverage = leverage;
      const sizeWithoutLeverage = new Decimal(size || "0").div(oldLeverage > 0 ? oldLeverage : 1);
      setLeverage(newLeverage);

      const newSize = sizeWithoutLeverage.mul(newLeverage);
      setSize(newSize.gt(0) ? newSize.toFixed(8) : "");

      if (selectedMarket?.pairSymbol) {
        savePreferredLeverage(exchangeId, selectedMarket.pairSymbol, newLeverage);
      }
    },
    [exchangeId, leverage, savePreferredLeverage, selectedMarket?.pairSymbol, setLeverage, setSize, size],
  );

  const orderDetailItems: PerpsOrderDetailItem[] = useMemo(() => {
    const quote = quoteAmount ? new Decimal(quoteAmount || "0") : new Decimal(0);
    const feeRate = new Decimal(config?.takerFee ?? 0.0005);
    const fee = quote.mul(feeRate);
    const margin = leverage > 0 ? quote.div(leverage).toFixed(2) : "0.00";

    return [
      { label: "Cost", value: `$${quote.toFixed(2)}` },
      { label: "Margin", value: `$${margin}` },
      { label: "Fee", value: `$${fee.toFixed(4)}` },
      { label: "Total", value: `$${quote.plus(fee).toFixed(2)}` },
    ];
  }, [config?.takerFee, leverage, quoteAmount]);

  const perpsOrderType: PerpsOrderType = orderType === OrderType.LIMIT ? "limit" : "market";
  const perpsSide: PerpsSide = orderSide;
  const perpsMarginMode: PerpsMarginMode = marginMode === MarginMode.ISOLATED ? "isolated" : "cross";

  // Disable limit orders when EITHER the global kill-switch is on OR the
  // active epoch has `limitOrder === false` (per-campaign gate). Clamp any
  // stale OrderType.LIMIT in the persisted store back to OrderType.MARKET so
  // the limit-price input doesn't render and submit can't pick limit semantics.
  const isLimitOrdersKillSwitchOn = useFeatureFlagIsOn(FEATURE_FLAGS.PERPS_HUB_DISABLE_LIMIT_ORDERS);
  const isLimitOrdersDisabled = isLimitOrdersKillSwitchOn || allowLimitOrders === false;
  useEffect(() => {
    if (isLimitOrdersDisabled && orderType === OrderType.LIMIT) {
      setOrderType(OrderType.MARKET);
    }
  }, [isLimitOrdersDisabled, orderType, setOrderType]);

  const handleOrderTypeChange = useCallback(
    (type: PerpsOrderType) => {
      const nextType = isLimitOrdersDisabled ? "market" : type;
      setOrderType(nextType === "limit" ? OrderType.LIMIT : OrderType.MARKET);
    },
    [isLimitOrdersDisabled, setOrderType],
  );

  // Binance rejects isolated margin while the account is in Multi-Assets Mode (-4168). Block it in the
  // UI: cross becomes the only option, so lock the selector to it. The user switches to Single-Asset
  // Mode on Binance to re-enable isolated.
  const isIsolatedMarginBlocked = exchangeId === CEDE_CEXS.BINANCE && marginAssetMode === "multi";
  const forcedMarginMode = config?.lockedMarginMode ?? (isIsolatedMarginBlocked ? MarginMode.CROSS : undefined);
  const marginModeReadOnly = forcedMarginMode !== undefined;

  useEffect(() => {
    if (forcedMarginMode !== undefined && marginMode !== forcedMarginMode) {
      setMarginMode(forcedMarginMode);
    }
  }, [forcedMarginMode, marginMode, setMarginMode]);

  const handleMarginModeChange = useCallback(
    (mode: PerpsMarginMode) => {
      if (marginModeReadOnly) return;
      setMarginMode(mode === "isolated" ? MarginMode.ISOLATED : MarginMode.CROSS);
    },
    [marginModeReadOnly, setMarginMode],
  );

  const handleSideChange = useCallback((side: PerpsSide) => {
    setOrderSide(side);
  }, []);

  const handleOrderSubmit = useCallback(async () => {
    if (!selectedMarket || !size || !account?.id) {
      if (showMissingInfoToast) {
        enqueueSnackbar("Missing required information", { variant: "error" });
      }
      return;
    }

    try {
      await createOrder({
        accountId: Number(account.id),
        symbol: selectedMarket.pairSymbol,
        side: orderSide === "long" ? ORDER_SIDE.BUY : ORDER_SIDE.SELL,
        type: orderType,
        amount: size,
        // Market order: no limit price, so send the same realtime mid the size was computed on
        // (keeps amount × price coherent). Fall back to the markPrice snapshot only if no live price.
        price: price || (marginPrice.gt(0) ? marginPrice.toString() : String(selectedMarket.markPrice)),
        leverage,
        marginMode,
        campaignId,
      });

      resetForm();
      setSizePercentage(0);
      onSubmitSuccess?.();
    } catch {
      // Error handled by useCexPerpsTrade.
    }
  }, [
    account?.id,
    campaignId,
    createOrder,
    leverage,
    marginMode,
    marginPrice,
    onSubmitSuccess,
    orderSide,
    orderType,
    price,
    resetForm,
    selectedMarket,
    showMissingInfoToast,
    size,
  ]);

  const handleConnectAccount = useCallback(() => {
    setExchangeIds([exchangeId as EXCHANGES]);
    setActiveStep(STEPS.ChooseCEX);
    setOpenConnectModal(true);
    onConnect?.();
  }, [exchangeId, onConnect, setActiveStep, setExchangeIds, setOpenConnectModal]);

  const amountError = useMemo(
    () => getMinOrderSizeError(size, selectedMarket?.minOrderSize, selectedMarket?.base),
    [size, selectedMarket?.minOrderSize, selectedMarket?.base],
  );
  const isFormValid = useMemo(() => getIsPositiveSize(size) && !amountError, [size, amountError]);
  const usdEquivalent = quoteAmount && quoteAmount !== "0.00" ? quoteAmount : undefined;
  const balanceDisplay = isBalanceLoading || !balance ? "..." : `${balance} ${selectedMarket?.quote || "USD"}`;
  const marketLabel = selectedMarket ? `${selectedMarket.base}/${selectedMarket.quote}` : "Perpetual";
  const buttonVariant =
    isAccountConnected && !shouldBlockSocials ? (orderSide === "long" ? "primary" : "destructive") : "primary";
  const buttonText = !isAccountConnected
    ? "Connect CEX Account"
    : shouldBlockSocials
      ? "Connect social accounts"
      : isCreatingOrder
        ? "Submitting..."
        : orderSide === "long"
          ? "Open Long"
          : "Open Short";

  return {
    exchangeId,
    config,
    selectedMarket,
    isAccountConnected,
    isCreatingOrder,
    isBalanceLoading,
    maxLeverage,
    orderType,
    perpsOrderType,
    orderSide,
    perpsSide,
    marginMode,
    perpsMarginMode,
    marginModeReadOnly,
    isIsolatedMarginBlocked,
    leverage,
    size,
    price,
    quoteAmount,
    usdEquivalent,
    sizeMode,
    sizePercentage,
    currentPrice,
    balanceDisplay,
    marketLabel,
    validationError,
    amountError,
    orderDetailItems,
    buttonVariant,
    buttonText,
    isSubmitDisabled: isAccountConnected && !shouldBlockSocials && (!isFormValid || isCreatingOrder || !selectedMarket),
    isLimitOrdersDisabled,
    shouldBlockSocials,
    handleConnectSocials,
    leverageModalOpen,
    setLeverageModalOpen,
    setSizeMode,
    setPrice,
    handleOrderTypeChange,
    handleMarginModeChange,
    handleSideChange,
    handleUnifiedSizeChange,
    handlePercentageChange,
    handleLeverageConfirm,
    handleOrderSubmit,
    handleConnectAccount,
  };
}
